/**
 * utils/coggins — the WEB TWIN of `backend/src/common/coggins.ts`.
 *
 * ⚠ KEEP THE PATTERN AND THE PROBES BYTE-IDENTICAL TO THE BACKEND FILE. There is
 * no filesystem drift guard in this repo and inventing one is not the house
 * pattern — the `common/soap-plan-dates.ts` ↔ `dateUtils.ts` and
 * `common/payment-date.ts` ↔ `dateUtils.formatPaymentDay` twins are held by this
 * comment plus a MIRRORED spec table (`coggins.test.ts` here,
 * `backend/src/common/coggins.spec.ts` there). Change one, change both.
 *
 * WHY THE WEB NEEDS ITS OWN COPY AT ALL. The chart's Coggins badge picks the
 * newest Coggins out of the `['lab-results', horseId]` array the page has
 * ALREADY fetched (HorseProfileClassic / HorseProfileModern) rather than firing a
 * new request, and the Labs panel decorates the matching row in place. Both are
 * client-side decisions over rows the server did not pre-classify.
 *
 * WHAT GOES WRONG IF THE TWO DRIFT: the backend arm generates a client-facing
 * reminder (email + SMS, `type: 'coggins'`) for a row the chart then renders as
 * "No record" — the exact complaint this feature exists to fix, restated one
 * layer up.
 */

/**
 * Word-boundary anchored so "Feia" and "Media" do NOT match while
 * "Coggins", "EIA", "Annual Coggins Test" and "Equine Infectious Anemia" do.
 */
export const COGGINS_PATTERN = /\b(coggins|eia|equine\s+infectious\s+anemia)\b/i;

/**
 * IDEXX reflab test codes that ARE an EIA test. Mirrors the backend set, which
 * is seeded from ONE observed value (`idexx-panel.service.ts`, panel
 * 'Coggins (EIA)', `testCodes: ['7073']`). An unknown EIA code is a SILENT MISS,
 * not an error; the two NAME probes below are the mitigation.
 */
export const COGGINS_IDEXX_TEST_CODES = new Set(['7073']);

/*
 * ⚠ NO `COGGINS_VALIDITY_MONTHS` ANY MORE — here or in the backend twin, which
 * carries the same tombstone. It was 12, and the web used it for exactly one
 * thing: pre-filling the quick entry's expiry at +12 months. The practice
 * manager reviewing the feature asked for that to stop ("don't default the
 * expiration to 12 months, i guess it varies"), and she is right — several US
 * states accept a 6-month Coggins, some events require a test inside 6 months,
 * and a few jurisdictions run the clock from the DRAW date. Nothing in this
 * product encodes a jurisdiction (`Clinic` has an address but no state-rule
 * table, `Horse` has no destination), so a guessed expiry is a number the chart
 * would state as fact. The field now stays BLANK until a human types what the
 * certificate says. If per-clinic validity is ever wanted it belongs on a
 * `ClinicReminderSettings`-style satellite, not on `Clinic` and not here.
 */

/** The panel name the chart's own "Record Coggins" quick entry posts. */
export const COGGINS_PANEL_NAME = 'Coggins (EIA)';

/**
 * The badge for "there IS a Coggins on file, and we cannot tell you when it
 * expires" — ONE string for ONE fact, shared by both chart layouts.
 *
 * It is the state EVERY pre-existing row lands in: no LabResult has an
 * `expiryDate` (the migration is additive with no backfill, deliberately — a
 * manufactured expiry would feed the generator a due date it would EMAIL an
 * owner about), and a legacy Coggins `Vaccination` may have a null
 * `nextDueDate`. So it has to be visually distinct from BOTH neighbours:
 * "No record" is the complaint this feature exists to fix and is now simply
 * false for these rows, while a bare "On file" reads as "fine" and would hide
 * an expiry nobody has recorded. Saying "expiry unknown" out loud is also the
 * affordance that gets a human to open Edit and type it — that is the backfill
 * for the historical rows: a UI state, not a script.
 */
export const COGGINS_NO_EXPIRY_LABEL = 'On file · expiry unknown';

/**
 * A stored Coggins result that says the horse is a CARRIER. The result value is
 * free text from whoever recorded it ("Positive", "POS", "Reactive", IDEXX's
 * upper-case forms); anything else - Negative, NEG, a titre, blank - is not
 * positive. Only rows whose test name says Coggins are consulted when such
 * rows exist, so a control row ("Control: Reactive") on an IDEXX panel cannot
 * flip a negative horse; a panel with one generic row falls back to that row.
 */
export const COGGINS_POSITIVE_PATTERN = /^\s*(pos(itive)?|reactive|detected)\b/i;

export function isPositiveCogginsResult(results: unknown): boolean {
  if (!Array.isArray(results)) return false;
  const rows = results.filter((r): r is { testName?: unknown; value?: unknown } => !!r && typeof r === 'object');
  const named = rows.filter((r) => isCogginsName(typeof r.testName === 'string' ? r.testName : null));
  const pool = named.length > 0 ? named : rows;
  return pool.some((r) => COGGINS_POSITIVE_PATTERN.test(String(r.value ?? '')));
}

export const isCogginsName = (name?: string | null): boolean =>
  COGGINS_PATTERN.test(name ?? '');

/** Deliberately structural, not an API type, so an un-saved form draft can be
 *  classified with the same rule the saved row will be. */
export interface CogginsCandidate {
  panelName?: string | null;
  results?: unknown;
  /** `LabRequest.testCodes`, when the row came from an order we placed. */
  labRequestTestCodes?: string[] | null;
}

/**
 * Is this LabResult an EIA / Coggins test?
 *
 * ⚠ `panelName` ALONE IS NOT ENOUGH, and that is the whole reason for the other
 * two probes. The IDEXX ORDER panel is named 'Coggins (EIA)', but the poller does
 * NOT copy that name onto the result: `idexx-result-mapper.service.ts` writes
 * `panelName` from the PROVIDER's `categories[].name`, which may read "Serology"
 * and never contain the word Coggins. So:
 *
 *   1. `panelName` — the manual quick-entry and legacy-import path.
 *   2. `results[].testName` / `results[].category` — `flattenAnalytes` writes
 *      both on every analyte row, and they survive because the IDEXX arm skips
 *      `validateLabResultItems` (which would strip `category`).
 *   3. `LabRequest.testCodes` — the definitive signal for an order we placed.
 *      `LabRequest` has no panel-name column, so `testCodes` is the only thing
 *      that survives from the ordered panel.
 */
export function isCogginsLabResult(row: CogginsCandidate): boolean {
  if (isCogginsName(row.panelName)) return true;
  if (Array.isArray(row.results)) {
    for (const r of row.results as Array<Record<string, unknown>>) {
      if (r && typeof r === 'object') {
        if (isCogginsName(r.testName as string | null | undefined)) return true;
        if (isCogginsName(r.category as string | null | undefined)) return true;
      }
    }
  }
  return (row.labRequestTestCodes ?? []).some((c) => COGGINS_IDEXX_TEST_CODES.has(c));
}

/**
 * The same classifier over a `GET /lab-results` ROW, which is where probe 3
 * comes from on the web.
 *
 * `LabResult.labRequestId` has always been on the wire (the read is an
 * un-narrowed `findMany`), but the ordered CODES are on the `LabRequest` row, not
 * the result — so `lab-results.service.ts` includes them on the two LIST reads
 * expressly to give this function its third probe. Without it the web would run
 * a two-probe classifier against a three-probe reminder generator, and an
 * IDEXX Coggins whose provider categories only say "Serology" would email the
 * owner while the chart said "No record".
 *
 * Tolerant of the shapes the row can actually arrive in: the online response
 * (nested `labRequest`), an offline replica row read straight from Dexie (no
 * relation — `/sync` pull is a different, un-included query, so probe 3 is
 * simply absent offline), and an optimistic row built from a POST body.
 */
export function isCogginsLabResultRow(row: {
  panelName?: string | null;
  results?: unknown;
  labRequest?: { testCodes?: string[] | null } | null;
} | null | undefined): boolean {
  if (!row) return false;
  return isCogginsLabResult({
    panelName: row.panelName,
    results: row.results,
    labRequestTestCodes: row.labRequest?.testCodes ?? null,
  });
}
